import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Button,
  CircularProgress,
  Fade,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import {
  Description,
  Download,
  Cancel,
  Visibility,
  Gesture,
  CheckCircle,
  Schedule,
  Warning,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AxiosResponse } from "axios";
import { DateTime } from "luxon";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";

const statusMap: any = {
  in_progress: {
    label: "In Progress",
    color: "#d97706",
    bgColor: "rgba(217, 119, 6, 0.1)",
    icon: <Schedule sx={{ fontSize: "16px" }} />,
  },
  signed: {
    label: "Signed",
    color: "#059669",
    bgColor: "rgba(5, 150, 105, 0.1)",
    icon: <CheckCircle sx={{ fontSize: "16px" }} />,
  },
  not_started: {
    label: "Not Started",
    color: "#64748b",
    bgColor: "rgba(100, 116, 139, 0.1)",
    icon: <Warning sx={{ fontSize: "16px" }} />,
  },
  provider_signed: {
    label: "Completed",
    color: "#059669",
    bgColor: "rgba(5, 150, 105, 0.1)",
    icon: <CheckCircle sx={{ fontSize: "16px" }} />,
  },
  verified: {
    label: "Verified",
    color: "#1d4ed8",
    bgColor: "rgba(29, 78, 216, 0.1)",
    icon: <CheckCircle sx={{ fontSize: "16px" }} />,
  },
};

const STUDYID = process.env.REACT_APP_REGISTRY_STUDY_ID;

const CoreConsents: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingRowId, setDownloadingRowId] = useState<string>("");
  const [revokingRowId, setRevokingRowId] = useState<string>("");
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [selectedConsentForRevoke, setSelectedConsentForRevoke] =
    useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Simulate API loading

        // Commented out API call due to failing API

        const url = `/participant/study/${STUDYID}/econsent?page=1&size=20`;

        const res: AxiosResponse = await http.get(url);
        const responseData = res.data?.data;
        const newData = responseData?.rows?.map((item: any) => ({
          id: item?.consentVersion?.id,
          name: item?.consentVersion?.name,
          version: item?.consentVersion?.version,
          consentId: item?.consentVersion?.consentId,
          consentDate: item?.signedOn
            ? DateTime.fromISO(item?.signedOn).toFormat("MMM dd, yyyy")
            : null,
          status: item?.status,
          sentOn: item?.sentOn
            ? DateTime.fromISO(item?.sentOn).toFormat("MMM dd, yyyy")
            : null,
          description:
            item?.consentVersion?.description ||
            "Core registry consent form for research participation",
        }));

        setData(newData || []);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, []);

  const handleDownload = async (versionId: string, consentId: string) => {
    try {
      setDownloadingRowId(versionId);
      const res = await http.post(
        `/participant/study/${STUDYID}/econsent/${consentId}/versions/${versionId}/download`
      );
      const downloadLink = res?.data?.data?.downloadUrl;
      window.open(downloadLink, "_blank");
      toastMessage("success", res.data.message);
      setDownloadingRowId("");
    } catch (err) {
      setDownloadingRowId("");
      errorToastMessage(err as Error);
    }
  };

  const handleRevokeConsentClick = (consent: any) => {
    setSelectedConsentForRevoke(consent);
    setRevokeModalOpen(true);
  };

  const handleRevokeConsentConfirm = async () => {
    if (!selectedConsentForRevoke) return;

    try {
      setRevokingRowId(selectedConsentForRevoke.id);
      setRevokeModalOpen(false);

      await http.post(`/participants/me/studies/${STUDYID}/revoke-consent`);

      toastMessage("success", "Consent revoked successfully");
      // Refresh data after revoking
      window.location.reload();
      setRevokingRowId("");
      setSelectedConsentForRevoke(null);
    } catch (err) {
      setRevokingRowId("");
      errorToastMessage(err as Error);
      setSelectedConsentForRevoke(null);
    }
  };

  const handleRevokeModalClose = () => {
    setRevokeModalOpen(false);
    setSelectedConsentForRevoke(null);
  };

  const handleSignConsent = (consentId: string, versionId: string) => {
    navigate(`/sign/${consentId}?versionId=${versionId}&studyId=${STUDYID}`);
  };

  const handleViewConsent = (consentId: string, versionId: string) => {
    navigate(
      `/sign/${consentId}?versionId=${versionId}&studyId=${STUDYID}&view=true`
    );
  };

  const getStatusInfo = (status: string) => {
    return statusMap[status] || statusMap.not_started;
  };

  const canDownload = (status: string) => {
    return (
      status === "signed" ||
      status === "provider_signed" ||
      status === "verified"
    );
  };

  const canRevoke = (status: string) => {
    return status === "signed" || status === "provider_signed";
  };

  const canSign = (status: string) => {
    return status === "not_started" || status === "in_progress";
  };

  const canView = (status: string) => {
    return (
      status === "signed" ||
      status === "provider_signed" ||
      status === "verified"
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 2, sm: 3, md: 4 },
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontSize: { xs: "20px", sm: "22px", md: "24px" },
          }}
        >
          Core Registry Consents
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            fontSize: { xs: "14px", sm: "16px" },
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          Manage your core registry consent forms and research participation
          agreements
        </Typography>
      </Box>

      {/* Loading State */}
      {loading ? (
        <Fade in={loading}>
          <Stack spacing={3}>
            {[1, 2, 3].map((index) => (
              <Card
                key={index}
                sx={{
                  borderRadius: "16px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  border: "1px solid rgba(226, 232, 240, 0.6)",
                  background: "rgba(255, 255, 255, 0.9)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2.5}>
                    {/* Header Row Skeleton */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          <Skeleton
                            variant="rectangular"
                            width={48}
                            height={48}
                            sx={{ borderRadius: "12px" }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton
                              variant="text"
                              width="70%"
                              height={24}
                              sx={{ borderRadius: "4px", mb: 0.5 }}
                            />
                            <Skeleton
                              variant="text"
                              width="40%"
                              height={16}
                              sx={{ borderRadius: "4px" }}
                            />
                          </Box>
                        </Box>
                        <Skeleton
                          variant="text"
                          width="90%"
                          height={16}
                          sx={{ borderRadius: "4px", mb: 1 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          <Skeleton
                            variant="text"
                            width={120}
                            height={16}
                            sx={{ borderRadius: "4px" }}
                          />
                          <Skeleton
                            variant="text"
                            width={100}
                            height={16}
                            sx={{ borderRadius: "4px" }}
                          />
                        </Box>
                      </Box>
                      <Skeleton
                        variant="rectangular"
                        width={80}
                        height={32}
                        sx={{ borderRadius: "20px" }}
                      />
                    </Box>

                    {/* Actions Row Skeleton */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pt: 1,
                        borderTop: "1px solid rgba(226, 232, 240, 0.6)",
                      }}
                    >
                      <Skeleton
                        variant="text"
                        width={140}
                        height={14}
                        sx={{ borderRadius: "4px" }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Skeleton
                          variant="rectangular"
                          width={70}
                          height={32}
                          sx={{ borderRadius: "12px" }}
                        />
                        <Skeleton
                          variant="rectangular"
                          width={80}
                          height={32}
                          sx={{ borderRadius: "12px" }}
                        />
                        <Skeleton
                          variant="rectangular"
                          width={90}
                          height={32}
                          sx={{ borderRadius: "12px" }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Fade>
      ) : data.length > 0 ? (
        /* Consent Cards */
        <Stack spacing={3}>
          {data.map((item: any) => {
            const statusInfo = getStatusInfo(item?.status);
            return (
              <Card
                key={item.id}
                sx={{
                  borderRadius: "16px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  border: "1px solid rgba(226, 232, 240, 0.6)",
                  background: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow:
                      "0 4px 20px rgba(0, 0, 0, 0.08), 0 0 30px rgba(29, 78, 216, 0.08)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2.5}>
                    {/* Header Row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: "12px",
                              backgroundColor: "primary.main",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Description sx={{ fontSize: "20px" }} />
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: { xs: "16px", sm: "18px" },
                                mb: 0.5,
                              }}
                            >
                              {item?.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#64748b",
                                fontSize: "12px",
                                fontWeight: 500,
                              }}
                            >
                              Version {item?.version}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontSize: "14px",
                            lineHeight: 1.5,
                            mb: 1,
                          }}
                        >
                          {item?.description}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          {item?.sentOn && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#64748b",
                                fontSize: "12px",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Schedule
                                sx={{ fontSize: "14px", color: "#64748b" }}
                              />
                              Sent {item.sentOn}
                            </Typography>
                          )}
                          {item?.consentDate && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#059669",
                                fontSize: "12px",
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <CheckCircle
                                sx={{ fontSize: "14px", color: "#10b981" }}
                              />
                              Signed {item.consentDate}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Chip
                        icon={statusInfo.icon}
                        label={statusInfo.label}
                        sx={{
                          borderRadius: "20px",
                          px: 2,
                          py: 0.5,
                          fontSize: "12px",
                          fontWeight: 600,
                          color: statusInfo.color,
                          bgcolor: statusInfo.bgColor,
                          border: `1px solid ${statusInfo.color}20`,
                          "& .MuiChip-icon": {
                            fontSize: "16px",
                            color: statusInfo.color,
                          },
                        }}
                      />
                    </Box>

                    {/* Actions Row */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pt: 1,
                        borderTop: "1px solid rgba(226, 232, 240, 0.6)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#94a3b8",
                          fontSize: "11px",
                          fontWeight: 500,
                        }}
                      >
                        Core Registry Consent
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {canView(item?.status) && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() =>
                              handleViewConsent(item?.consentId, item?.id)
                            }
                            sx={{ textTransform: "none" }}
                          >
                            View
                          </Button>
                        )}
                        {canSign(item?.status) && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Gesture />}
                            onClick={() =>
                              handleSignConsent(item?.consentId, item?.id)
                            }
                            sx={{ textTransform: "none" }}
                          >
                            Sign
                          </Button>
                        )}
                        {canDownload(item?.status) && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                              downloadingRowId === item?.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Download />
                              )
                            }
                            onClick={() =>
                              handleDownload(item?.id, item?.consentId)
                            }
                            disabled={downloadingRowId === item?.id}
                            sx={{ textTransform: "none" }}
                          >
                            Download
                          </Button>
                        )}
                        {canRevoke(item?.status) && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={
                              revokingRowId === item?.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Cancel />
                              )
                            }
                            onClick={() => handleRevokeConsentClick(item)}
                            disabled={revokingRowId === item?.id}
                            sx={{ textTransform: "none" }}
                          >
                            Revoke
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        /* Empty State */
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            textAlign: "center",
            p: 4,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #F4E9FF 0%, #EBD9FF 100%)",
              mb: 3,
            }}
          >
            <Description sx={{ fontSize: "48px", color: "primary.main" }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#64748b",
              mb: 2,
              fontSize: { xs: "20px", sm: "24px" },
            }}
          >
            No Core Consents Available
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#64748b",
              maxWidth: "400px",
              lineHeight: 1.6,
            }}
          >
            There are currently no core registry consent forms available. Check
            back later for updates.
          </Typography>
        </Box>
      )}

      {/* Revoke Consent Modal */}
      <Dialog
        open={revokeModalOpen}
        onClose={handleRevokeModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: "1px solid rgba(226, 232, 240, 0.6)",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#dc2626",
              fontSize: { xs: "18px", sm: "20px" },
            }}
          >
            Revoking Your Registry Consent
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Stack spacing={3}>
            {/* Warning Message */}
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: "rgba(220, 38, 38, 0.05)",
                border: "1px solid rgba(220, 38, 38, 0.1)",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "#dc2626",
                  fontWeight: 600,
                  fontSize: "16px",
                  mb: 1,
                }}
              >
                You are about to revoke your consent for the{" "}
                <span style={{ fontWeight: 700 }}>
                  {selectedConsentForRevoke?.name || "Core Registry"}
                </span>
                .
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  lineHeight: 1.6,
                }}
              >
                By doing this, you are requesting that your information no
                longer be used for research studies. This action will stop the
                registry from collecting and sharing any new health information
                about you for research purposes.
              </Typography>
            </Box>

            {/* What happens section */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#0f172a",
                  mb: 2,
                  fontSize: "16px",
                }}
              >
                What happens to your information after you revoke consent?
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "#374151",
                      mb: 1,
                    }}
                  >
                    Existing data:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#64748b",
                      lineHeight: 1.6,
                    }}
                  >
                    Any health information already collected and de-identified
                    for past or ongoing studies may continue to be used as
                    outlined in the original consent forms for those studies.
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "#374151",
                      mb: 1,
                    }}
                  >
                    Future use:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#64748b",
                      lineHeight: 1.6,
                    }}
                  >
                    No new health information will be collected or shared with
                    researchers from the date you revoke your consent.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Important note */}
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: "rgba(245, 158, 11, 0.05)",
                border: "1px solid rgba(245, 158, 11, 0.1)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#f59e0b",
                  fontWeight: 600,
                  lineHeight: 1.6,
                }}
              >
                Please note: This action will remove you as a participant from
                all research studies associated with this registry. You will no
                longer receive communications about new studies and your
                information will not be included in future research.
              </Typography>
            </Box>

            {/* Final confirmation */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#0f172a",
                  textAlign: "center",
                  fontSize: "16px",
                }}
              >
                Are you sure you want to proceed with revoking your consent for
                the{" "}
                <span style={{ fontWeight: 700 }}>
                  {selectedConsentForRevoke?.name || "Core Registry"}
                </span>
                ?
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            p: 3,
            gap: 2,
            justifyContent: "center",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleRevokeModalClose}
            sx={{
              minWidth: "140px",
              borderColor: "#64748b",
              color: "#64748b",
              "&:hover": {
                borderColor: "#475569",
                backgroundColor: "rgba(100, 116, 139, 0.05)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRevokeConsentConfirm}
            color="error"
            sx={{
              minWidth: "140px",
              backgroundColor: "#dc2626",
              "&:hover": {
                backgroundColor: "#b91c1c",
              },
            }}
          >
            Yes, Revoke Consent
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoreConsents;
